import clsx from "clsx";
import { helpers } from "../../util/helpers.ts";
import { createMobileCrossTabHref } from "../../util/mobileCrossTabLink.ts";
import { useLocal } from "../../util/local.ts";
import { TeamLogoInline } from "../../components/TeamLogoInline.tsx";
import type { LocalStateUI } from "../../../../../common/types.ts";
import { PlayerNameLabels } from "../../components/PlayerNameLabels.tsx";
import { DraftButtons } from "./DraftButtons.tsx";
import i18n from "../../util/i18n.ts";
import {
	translateTeamRegion,
	translateTeamName,
	translateTeamShortName,
} from "../../util/teamNames.ts";
import { PositionText } from "../../util/positionNames.tsx";

type TeamInfo = LocalStateUI["teamInfoCache"][number] | undefined;

const Logo = ({
	t,
	tid,
}: {
	t: TeamInfo;
	tid: number;
}) => {
	if (!t) {
		return null;
	}

	return (
		<a href={createMobileCrossTabHref(helpers.leagueUrl(["roster", `${t.abbrev}_${tid}`]))}>
			<TeamLogoInline
				imgURL={t.imgURL}
				imgURLSmall={t.imgURLSmall}
				includePlaceholderIfNoLogo
				size={32}
			/>
		</a>
	);
};

const PickTeam = ({
	draft,
	lineBreak,
	prefix,
	t,
	tid,
}: {
	draft: {
		round: number;
		pick: number;
	};
	lineBreak?: boolean;
	prefix?: string;
	t: TeamInfo;
	tid: number;
}) => {
	if (!t) {
		return null;
	}

	return (
		<>
			<span className="d-none d-sm-inline">{prefix}</span>
			{draft.round}-{draft.pick}
			{lineBreak ? <br /> : " "}
			<a href={createMobileCrossTabHref(helpers.leagueUrl(["roster", `${t.abbrev}_${tid}`]))}>
				<span className="d-md-none">{translateTeamShortName(t, t.abbrev)}</span>
				<span className="d-none d-md-inline">
					{translateTeamRegion(t.region)} {translateTeamName(t.name)}
				</span>
			</a>
		</>
	);
};

const PickWithoutPlayers = ({
	draft,
	prefix,
	t,
}: {
	draft: {
		round: number;
		pick: number;
		tid: number;
	};
	prefix?: string;
	t: TeamInfo;
}) => {
	return (
		<div className="d-flex align-items-center gap-2">
			{/* B2c 法则二: the team name right beside it is the single entity link
			    for the on-clock team, so the logo stays unlinked. */}
			{t ? (
				<TeamLogoInline
					imgURL={t.imgURL}
					imgURLSmall={t.imgURLSmall}
					includePlaceholderIfNoLogo
					size={32}
				/>
			) : null}
			<div>
				<PickTeam
					draft={draft}
					lineBreak
					t={t}
					prefix={prefix}
					tid={draft.tid}
				/>
			</div>
		</div>
	);
};

const NextDraftText = ({
	season,
	userNextPickYear,
}: {
	season: number;
	userNextPickYear: number;
}) => {
	if (userNextPickYear <= season) {
		return i18n.t("draft.nextDraft", { ns: "ui", defaultValue: "next draft" });
	} else if (userNextPickYear === season + 1) {
		return i18n.t("draft.nextYear", { ns: "ui", defaultValue: "next year" });
	} else {
		return i18n.t("draft.inYear", { ns: "ui", defaultValue: "in {{year}}!", year: userNextPickYear });
	}
};

const getPickTeamLabel = (t: TeamInfo, tid: number) =>
	t ? translateTeamShortName(t, t.abbrev) : `${tid}`;

const YoureUpValue = ({
	numPicks,
	season,
	userNextPickYear,
}: {
	numPicks: number;
	season: number;
	userNextPickYear: number;
}) => {
	if (numPicks < 0) {
		return (
			<NextDraftText season={season} userNextPickYear={userNextPickYear} />
		);
	} else if (numPicks === 0) {
		return <b>{i18n.t("draft.youreUpNow", { ns: "ui", defaultValue: "now!" })}</b>;
	} else if (numPicks === 1) {
		return <b>{i18n.t("draft.youreUpNext", { ns: "ui", defaultValue: "next!" })}</b>;
	}

	return (
		<>
			{i18n.t("draft.youreUpIn", { ns: "ui", defaultValue: "in {{count}} picks", count: numPicks })}
		</>
	);
};

const YoureUp = ({
	numPicks,
	season,
	userNextPickYear,
}: {
	numPicks: number;
	season: number;
	userNextPickYear: number;
}) => {
	const color =
		numPicks === 0
			? "bg-success"
			: numPicks === 1
				? "bg-warning"
				: "bg-secondary";

	return (
		<div
			className={clsx(
				"ps-3 pe-2 text-end rounded-start-pill align-self-stretch d-flex align-items-center",
				color,
			)}
		>
			<div>
				{i18n.t("draft.youreUp", { ns: "ui", defaultValue: "My pick:" })}
				<br />
				<YoureUpValue
					numPicks={numPicks}
					season={season}
					userNextPickYear={userNextPickYear}
				/>
			</div>
		</div>
	);
};

const YoureUpMobile = ({
	numPicks,
	season,
	userNextPickYear,
}: {
	numPicks: number;
	season: number;
	userNextPickYear: number;
}) => {
	const color =
		numPicks === 0
			? "bg-success"
			: numPicks === 1
				? "bg-warning"
				: "bg-secondary";

	return (
		<div className={clsx("draft-mobile-youre-up", color)}>
			<div className="small">
				{i18n.t("draft.youreUp", { ns: "ui", defaultValue: "My pick:" })}
			</div>
			<div className="fw-semibold">
				<YoureUpValue
					numPicks={numPicks}
					season={season}
					userNextPickYear={userNextPickYear}
				/>
			</div>
		</div>
	);
};

export const StickyDraftInfo = ({
	challengeNoRatings,
	drafted,
	season,
	spectator,
	userNextPickYear,
	userTids,
}: {
	challengeNoRatings: boolean;
	drafted: any[];
	season: number;
	spectator: boolean;
	userNextPickYear: number;
	userTids: number[];
}) => {
	const teamInfoCache = useLocal((state) => state.teamInfoCache);

	const currentPickIndex = drafted.findIndex((p) => p.pid === -1);
	if (currentPickIndex === -1) {
		return null;
	}

	const currentPick = drafted[currentPickIndex].draft;
	const prevPick = drafted[currentPickIndex - 1];
	const nextPick = drafted[currentPickIndex + 1]?.draft;

	const yourNextPickIndex = drafted.findIndex(
		(p, i) => i >= currentPickIndex && userTids.includes(p.draft.tid),
	);
	const yourNextPick = yourNextPickIndex - currentPickIndex;

	const userRemaining = yourNextPickIndex !== -1;
	const usersTurn = yourNextPick === 0;
	const currentPickTeam = teamInfoCache[currentPick.tid];
	const currentPickSummary = i18n.t("draft.stickyCurrentPickSummary", {
		ns: "ui",
		pick: currentPick.pick,
		round: currentPick.round,
		team: getPickTeamLabel(currentPickTeam, currentPick.tid),
		defaultValue: "Current #{{round}}-{{pick}} {{team}}",
	});
	// Treat as equal size, unless we need to shrink
	const flex = "1 1 0";

	return (
		<div
			className="sticky-top mb-3 text-nowrap draft-sticky-info"
			data-draft-sticky-info-host=""
			data-draft-sticky-current-pick-number={currentPick.pick}
			data-draft-sticky-current-pick-round={currentPick.round}
			data-draft-sticky-current-pick-tid={currentPick.tid}
			style={{
				marginLeft: "-0.5rem",
				marginRight: "-0.5rem",
				top: "52px",
				pointerEvents: "none",
			}}
		>
			<div
				className="d-none d-sm-flex align-items-center bg-secondary-very-subtle"
				style={{
					paddingLeft: "0.5rem",
					pointerEvents: "auto",
				}}
			>
				<div className="d-none d-xxl-block me-3">
					<DraftButtons
						spectator={spectator}
						userRemaining={userRemaining}
						usersTurn={usersTurn}
					/>
				</div>
				<div className="d-flex flex-grow-1 gap-1 py-1">
					<div
						data-draft-sticky-previous-pick={prevPick ? "true" : undefined}
						data-draft-sticky-previous-pick-pid={prevPick?.pid}
						style={{ flex }}
					>
						<div>
							{prevPick ? (
								<>
									<div className="d-flex align-items-center gap-2">
										<Logo
											t={teamInfoCache[prevPick.draft.tid]}
											tid={prevPick.draft.tid}
										/>
										<div>
											<PickTeam
												draft={prevPick.draft}
												t={teamInfoCache[prevPick.draft.tid]}
												tid={prevPick.draft.tid}
											/>

											<div>
												<span className="d-none d-md-inline">
													<PositionText
														pos={prevPick.ratings.pos}
														display="full"
													/>{" "}
												</span>
												<PlayerNameLabels
													pid={prevPick.pid}
													injury={prevPick.injury}
													skills={prevPick.ratings.skills}
													defaultWatch={prevPick.watch}
													firstName={prevPick.firstName}
													firstNameShort={prevPick.firstNameShort}
													lastName={prevPick.lastName}
												/>
												<span className="d-none d-md-inline ps-1">
													{" "}
													{!challengeNoRatings
														? `${prevPick.ratings.ovr}/${prevPick.ratings.pot}, `
														: null}
													{prevPick.age} yo
												</span>
											</div>
										</div>
									</div>
								</>
							) : null}
						</div>
					</div>
					<div style={{ flex }}>
						<PickWithoutPlayers
							draft={currentPick}
							prefix={i18n.t("draft.currentPick", { ns: "ui", defaultValue: "Current pick: " })}
							t={teamInfoCache[currentPick.tid]}
						/>
						<div
							className="small text-body-secondary"
						>
							{currentPickSummary}
						</div>
					</div>
					<div className="d-none d-sm-block" style={{ flex }}>
						{nextPick ? (
							<PickWithoutPlayers
								draft={nextPick}
								prefix={i18n.t("draft.nextPick", { ns: "ui", defaultValue: "Next pick: " })}
								t={teamInfoCache[nextPick.tid]}
							/>
						) : (
							<div
								style={{
									// Account for no Logo
									paddingLeft: 40,
								}}
							>
								{i18n.t("draft.nextPickNone", { ns: "ui", defaultValue: "Next pick: none" })}
							</div>
						)}
					</div>
				</div>
				<YoureUp
					numPicks={yourNextPick}
					season={season}
					userNextPickYear={userNextPickYear}
				/>
			</div>
			<div className="d-sm-none bg-secondary-very-subtle draft-mobile-task-card">
				<div className="draft-mobile-task-card-main">
					<div>
						<div className="small text-secondary">
							{i18n.t("draft.currentPick", { ns: "ui", defaultValue: "Current pick: " })}
						</div>
						{/* B2c 法则二: the round-pick + team link above already carries the
						    current-pick info, so the mobile summary copy line is gone; the
						    summary stays machine-readable on the wrapper attribute. */}
						<PickWithoutPlayers
							draft={currentPick}
							t={teamInfoCache[currentPick.tid]}
						/>
					</div>
					<YoureUpMobile
						numPicks={yourNextPick}
						season={season}
						userNextPickYear={userNextPickYear}
					/>
				</div>
			</div>
			<div
				className="d-none d-sm-flex d-xxl-none"
				style={{
					paddingLeft: "0.5rem",
					pointerEvents: "none",
				}}
			>
				<div
					className="bg-secondary-very-subtle rounded-bottom pt-1"
					style={{
						pointerEvents: "auto",
					}}
				>
					<DraftButtons
						spectator={spectator}
						userRemaining={userRemaining}
						usersTurn={usersTurn}
					/>
				</div>
			</div>
		</div>
	);
};
